// ============================================================
// Hirafi — Module devis : formulaire de création standalone
// ============================================================

import { useState } from "react";
import { FileText, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { LineItemsEditor } from "./LineItemsEditor";
import type { StandaloneDevisFormData, QuoteLineItem } from "../types";
import {
  calcLineItemsTotal,
  minValidUntil,
  maxValidUntil,
  defaultValidUntil,
  formatAmount,
} from "../lib/calculations";

type LineItemDraft = Omit<QuoteLineItem, "id" | "quote_id">;

const EMPTY_FORM: StandaloneDevisFormData = {
  artisan: { name: "", email: "", phone: "", address: "" },
  client: { name: "", email: "", phone: "", address: "" },
  title: "",
  description: "",
  lineItems: [],
  totalAmount: 0,
  vatRate: undefined,
  currency: "DZD",
  validUntil: defaultValidUntil(),
};

interface Props {
  initialData?: Partial<StandaloneDevisFormData>;
  onPreview: (data: StandaloneDevisFormData) => void;
  onSave?: (data: StandaloneDevisFormData) => Promise<void>;
  saving?: boolean;
  isGuest?: boolean;
}

export function StandaloneDevisBuilder({
  initialData,
  onPreview,
  onSave,
  saving,
  isGuest,
}: Props) {
  const [form, setForm] = useState<StandaloneDevisFormData>({
    ...EMPTY_FORM,
    ...initialData,
    artisan: { ...EMPTY_FORM.artisan, ...initialData?.artisan },
    client: { ...EMPTY_FORM.client, ...initialData?.client },
  });
  const [isAmountManual, setIsAmountManual] = useState(false);
  const [showLineItems, setShowLineItems] = useState(true);
  const [vatEnabled, setVatEnabled] = useState(!!initialData?.vatRate);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = <K extends keyof StandaloneDevisFormData>(
    key: K,
    value: StandaloneDevisFormData[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const setArtisanField = (key: keyof StandaloneDevisFormData["artisan"], value: string) =>
    setForm((prev) => ({ ...prev, artisan: { ...prev.artisan, [key]: value } }));

  const setClientField = (key: keyof StandaloneDevisFormData["client"], value: string) =>
    setForm((prev) => ({ ...prev, client: { ...prev.client, [key]: value } }));

  const handleLineItemsChange = (items: LineItemDraft[]) => {
    setForm((prev) => {
      const auto = calcLineItemsTotal(items);
      return {
        ...prev,
        lineItems: items,
        totalAmount: !isAmountManual && items.length > 0 ? auto : prev.totalAmount,
      };
    });
  };

  const handleVatToggle = (enabled: boolean) => {
    setVatEnabled(enabled);
    setField("vatRate", enabled ? (form.vatRate ?? 19) : undefined);
  };

  // Montants calculés
  const ht = form.totalAmount;
  // BUG-DEVIS-002 : arrondi stable Math.round pour éviter la dérive flottante de toFixed/V8
  const vatAmount = vatEnabled && form.vatRate
    ? Math.round(ht * (form.vatRate / 100) * 100) / 100
    : 0;
  const ttc = Math.round((ht + vatAmount) * 100) / 100;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.artisan.name.trim()) e["artisan.name"] = "Nom de l'artisan requis";
    if (!form.artisan.email.trim()) e["artisan.email"] = "Email artisan requis";
    if (!form.artisan.phone.trim()) e["artisan.phone"] = "Téléphone artisan requis";
    if (!form.client.name.trim()) e["client.name"] = "Nom du client requis";
    if (!form.client.email.trim()) e["client.email"] = "Email client requis";
    if (!form.title.trim()) e.title = "Titre requis";
    if (!form.description.trim()) e.description = "Description requise";
    if (form.totalAmount <= 0) e.totalAmount = "Montant invalide";
    if (!form.validUntil) e.validUntil = "Date de validité requise";
    else if (form.validUntil < minValidUntil()) e.validUntil = "Date trop proche (min 3 jours)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePreview = () => {
    if (validate()) onPreview(form);
  };

  const handleSave = async () => {
    if (!validate() || !onSave) return;
    await onSave(form);
  };

  const err = (key: string) =>
    errors[key] ? (
      <p className="text-xs text-destructive mt-1">{errors[key]}</p>
    ) : null;

  return (
    <div className="space-y-8">
      {/* Section artisan */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-6 w-1 rounded-full bg-primary" />
          <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            Vos coordonnées
          </h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Nom / Raison sociale *</Label>
            <Input
              value={form.artisan.name}
              onChange={(e) => setArtisanField("name", e.target.value)}
              placeholder="Dupont Plomberie"
              className="h-10"
            />
            {err("artisan.name")}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Email *</Label>
            <Input
              type="email"
              value={form.artisan.email}
              onChange={(e) => setArtisanField("email", e.target.value)}
              placeholder="contact@exemple.dz"
              className="h-10"
            />
            {err("artisan.email")}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Téléphone *</Label>
            <Input
              type="tel"
              value={form.artisan.phone}
              onChange={(e) => setArtisanField("phone", e.target.value)}
              placeholder="05 XX XX XX XX"
              className="h-10"
            />
            {err("artisan.phone")}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Adresse</Label>
            <Input
              value={form.artisan.address ?? ""}
              onChange={(e) => setArtisanField("address", e.target.value)}
              placeholder="Rue, Ville, Wilaya"
              className="h-10"
            />
          </div>
        </div>
      </section>

      {/* Section client */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-6 w-1 rounded-full bg-accent" />
          <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            Informations client
          </h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Nom du client *</Label>
            <Input
              value={form.client.name}
              onChange={(e) => setClientField("name", e.target.value)}
              placeholder="M. Benali"
              className="h-10"
            />
            {err("client.name")}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Email client *</Label>
            <Input
              type="email"
              value={form.client.email}
              onChange={(e) => setClientField("email", e.target.value)}
              placeholder="client@exemple.dz"
              className="h-10"
            />
            {err("client.email")}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Téléphone</Label>
            <Input
              type="tel"
              value={form.client.phone ?? ""}
              onChange={(e) => setClientField("phone", e.target.value)}
              placeholder="06 XX XX XX XX"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Adresse client</Label>
            <Input
              value={form.client.address ?? ""}
              onChange={(e) => setClientField("address", e.target.value)}
              placeholder="Rue, Ville"
              className="h-10"
            />
          </div>
        </div>
      </section>

      {/* Section devis */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-6 w-1 rounded-full bg-orange-400" />
          <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            Détail du devis
          </h3>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Titre du devis *</Label>
          <Input
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Ex : Réparation fuite robinet cuisine"
            maxLength={100}
            className="h-10"
          />
          {err("title")}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Description des travaux *</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Décrivez les travaux à réaliser…"
            maxLength={1000}
            rows={3}
            className="resize-none"
          />
          {err("description")}
        </div>

        {/* Lignes de prestation */}
        <div className="space-y-3">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
            onClick={() => setShowLineItems((v) => !v)}
          >
            {showLineItems ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Détail des prestations{" "}
            <span className="text-xs font-normal text-muted-foreground">(optionnel)</span>
          </button>
          {showLineItems && (
            <LineItemsEditor items={form.lineItems} onChange={handleLineItemsChange} />
          )}
        </div>

        {/* Montant HT + validité */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">
              Montant {vatEnabled ? "HT" : "total"} (DZD) *
            </Label>
            <div className="relative">
              <Input
                type="number"
                min={0}
                value={form.totalAmount || ""}
                onChange={(e) => {
                  setIsAmountManual(true);
                  setField("totalAmount", parseFloat(e.target.value) || 0);
                }}
                placeholder="0"
                className="h-10 pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                DZD
              </span>
            </div>
            {isAmountManual && form.lineItems.length > 0 && (
              <button
                type="button"
                className="text-xs text-primary underline underline-offset-2"
                onClick={() => {
                  setIsAmountManual(false);
                  setField("totalAmount", calcLineItemsTotal(form.lineItems));
                }}
              >
                Recalculer depuis les prestations
              </button>
            )}
            {err("totalAmount")}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Valide jusqu'au *</Label>
            <Input
              type="date"
              value={form.validUntil}
              min={minValidUntil()}
              max={maxValidUntil()}
              onChange={(e) => setField("validUntil", e.target.value)}
              className="h-10"
            />
            {err("validUntil")}
          </div>
        </div>

        {/* TVA */}
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">TVA</p>
              <p className="text-xs text-muted-foreground">Appliquer une taxe sur la valeur ajoutée</p>
            </div>
            <Switch
              checked={vatEnabled}
              onCheckedChange={handleVatToggle}
            />
          </div>

          {vatEnabled && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-32 space-y-1">
                  <Label className="text-xs text-muted-foreground">Taux (%)</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={form.vatRate ?? ""}
                      onChange={(e) =>
                        setField("vatRate", parseFloat(e.target.value) || 0)
                      }
                      placeholder="19"
                      className="h-9 pr-7"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      %
                    </span>
                  </div>
                </div>
                <div className="flex-1 text-sm text-muted-foreground pt-5">
                  TVA = {formatAmount(vatAmount)}
                </div>
              </div>

              {/* Récapitulatif HT / TVA / TTC */}
              <div className="border-t pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Montant HT</span>
                  <span>{formatAmount(ht)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>TVA ({form.vatRate ?? 0}%)</span>
                  <span>{formatAmount(vatAmount)}</span>
                </div>
                <div className="flex justify-between font-semibold text-foreground border-t pt-1.5">
                  <span>Total TTC</span>
                  <span className="text-primary">{formatAmount(ttc)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
        <Button type="button" variant="outline" className="gap-2" onClick={handlePreview}>
          <Eye className="h-4 w-4" />
          Aperçu / PDF
        </Button>

        {!isGuest && onSave && (
          <Button
            type="button"
            variant="hero"
            className="gap-2"
            onClick={handleSave}
            disabled={saving}
          >
            <FileText className="h-4 w-4" />
            {saving ? "Sauvegarde…" : "Sauvegarder le devis"}
          </Button>
        )}
      </div>
    </div>
  );
}
